import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { getSupabase } from '../backend/supabaseAdapter';
import type { AuthOutcome } from '../state/sessionStore';

// Sign in with Apple, same lane discipline as src/native/googleSignIn.ts:
//   - the plugin is imported statically (a dynamic import() chunk that stalls on device is a
//     documented legacy failure mode we do not reproduce);
//   - native only — on the web the outcome says so instead of pretending;
//   - plugin cancellation REJECTS: backing out is a neutral outcome, never an error;
//   - the id token goes to Supabase (`signInWithIdToken`), the session then lives under the
//     yanwushi-v2-auth storage key like every other sign-in.
// Windows has no Xcode: type-checked and bundled here, behaviour on a device is DEVICE TEST REQUIRED.
let initialized = false;

export function appleSignInAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios' && getSupabase() !== null;
}

// What the Apple sheet's own failures mean (ASAuthorizationError; the plugin hands the raw error over,
// its text reads "… (com.apple.AuthenticationServices.AuthorizationError error 1001.)"):
//   1001  the inspector backed out of the sheet — neutral, like every other cancelled sign-in; the text
//         carries no word "cancel", so the code is what is read;
//   1000  the sheet never opened. On a signed build that is the missing "Sign In with Apple" capability
//         (Owner device test 2026-09-21, build 92: no entitlement in the app, none in the profile) or a
//         device with no Apple ID signed in. The raw code tells an inspector nothing; the message does.
export function classifyAppleSignInError(text: string): AuthOutcome {
  const code = /AuthorizationError\s+error\s+(\d+)/i.exec(text)?.[1] ?? null;
  if (code === '1001' || /cancel/i.test(text)) return { ok: false, code: 'cancelled', detail: '' };
  if (code === '1000') return { ok: false, code: 'appleUnavailable', detail: '' };
  return { ok: false, code: 'unknown', detail: text };
}

export async function signInWithApple(): Promise<AuthOutcome> {
  const client = getSupabase();
  if (!client) return { ok: false, code: 'notConfigured', detail: '' };
  if (!Capacitor.isNativePlatform()) return { ok: false, code: 'notNative', detail: '' };
  try {
    if (!initialized) {
      await SocialLogin.initialize({ apple: {} });
      initialized = true;
    }
    const login = (await SocialLogin.login({
      provider: 'apple',
      options: { scopes: ['email', 'name'] }
    })) as unknown as { result?: { idToken?: string | null } };
    const idToken = login.result?.idToken ?? null;
    if (!idToken) return { ok: false, code: 'unknown', detail: 'no idToken' };
    const { error } = await client.auth.signInWithIdToken({ provider: 'apple', token: idToken });
    if (error) return { ok: false, code: 'unknown', detail: error.message };
    return { ok: true, code: 'ok', detail: '' };
  } catch (raw) {
    return classifyAppleSignInError(raw instanceof Error ? raw.message : String(raw));
  }
}
